const aroundRC = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1], [0, 1],
    [1, -1], [1, 0], [1, 1],
];
// Simple seedable random number generator (Linear Congruential Generator).
class SeededRandom {
    constructor(seed) {
        this.seed = SeededRandom.stringToSeed(seed);
    }
    static stringToSeed(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return Math.abs(hash);
    }
    next() {
        this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
        return this.seed / 0x7fffffff;
    }
    nextInt(max) {
        return Math.floor(this.next() * max);
    }
}
function generateSeed() {
    const randomPart = Math.random().toString(36).substring(2, 10);
    return `${randomPart}-1`;
}
export function mineSweeper(ROWS, COLS, MINES) {
    function createEmptyBoard() {
        return Array.from({ length: ROWS }, () => Array.from({ length: COLS }, () => ({
            isMine: false,
            isRevealed: false,
            isFlagged: false,
            adjacentMines: 0,
        })));
    }
    // Mutates `board` in place: places mines + computes adjacency counts.
    // Returns the resolved seed string (with iteration applied).
    function generateBoardInPlace(board, safeR, safeC, seedStr = "", replay = false) {
        if (seedStr === "") {
            seedStr = generateSeed();
        }
        else if (!replay) {
            const parts = seedStr.split("-");
            if (parts.length < 2 || isNaN(parseInt(parts[1]))) {
                // Invalid seed format → silently fall back to a fresh seed.
                seedStr = generateSeed();
            }
            else {
                const iteration = parseInt(parts[1]) + 1;
                seedStr = `${parts[0]}-${iteration}`;
            }
        }
        const seed = seedStr.split("-")[0];
        const rng = new SeededRandom(seed);
        let minesPlaced = 0;
        while (minesPlaced < MINES) {
            const r = rng.nextInt(ROWS);
            const c = rng.nextInt(COLS);
            if ((r === safeR && c === safeC) || board[r][c].isMine)
                continue;
            board[r][c].isMine = true;
            minesPlaced++;
        }
        for (let r = 0; r < ROWS; r++) {
            for (let c = 0; c < COLS; c++) {
                if (board[r][c].isMine)
                    continue;
                let count = 0;
                for (const [dr, dc] of aroundRC) {
                    const nr = r + dr, nc = c + dc;
                    if (nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS && board[nr][nc].isMine) {
                        count++;
                    }
                }
                board[r][c].adjacentMines = count;
            }
        }
        return seedStr;
    }
    // Mutates `board` in place: flood-fills from (r, c).
    // Returns the number of cells revealed (0 for out-of-bounds / already revealed / flagged).
    function revealCellInPlace(board, r, c) {
        if (r < 0 || r >= ROWS || c < 0 || c >= COLS)
            return 0;
        const cell = board[r][c];
        if (cell.isRevealed || cell.isFlagged)
            return 0;
        const stack = [[r, c]];
        let revealedCount = 0;
        while (stack.length) {
            const [row, col] = stack.pop();
            const target = board[row][col];
            if (target.isRevealed || target.isFlagged)
                continue;
            target.isRevealed = true;
            revealedCount++;
            if (target.adjacentMines === 0 && !target.isMine) {
                for (const [dr, dc] of aroundRC) {
                    const nr = row + dr, nc = col + dc;
                    if (nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS) {
                        const nb = board[nr][nc];
                        if (!nb.isRevealed && !nb.isFlagged) {
                            stack.push([nr, nc]);
                        }
                    }
                }
            }
        }
        return revealedCount;
    }
    // Pure read: counts flagged neighbors of (r, c).
    function countFlaggedAround(board, r, c) {
        let count = 0;
        for (const [dr, dc] of aroundRC) {
            const nr = r + dr, nc = c + dc;
            if (nr >= 0 && nr < ROWS && nc >= 0 && nc < COLS && board[nr][nc].isFlagged) {
                count++;
            }
        }
        return count;
    }
    // Mutates `board` in place: chord-reveals neighbors.
    // If a mine is hit among neighbors, also reveals all mines (game-over UI).
    // Returns { count: safe cells revealed, minePos: optional first mine hit }.
    // Crucially: safe reveals are NOT discarded when a mine is also hit (regression fix).
    function revealAroundInPlace(board, r, c) {
        let count = 0;
        let minePos;
        for (const [dr, dc] of aroundRC) {
            const nr = r + dr, nc = c + dc;
            if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS)
                continue;
            const neighbor = board[nr][nc];
            if (neighbor.isFlagged || neighbor.isRevealed)
                continue;
            if (neighbor.isMine) {
                if (!minePos)
                    minePos = { r: nr, c: nc };
            }
            else {
                count += revealCellInPlace(board, nr, nc);
            }
        }
        if (minePos)
            revealAllMinesInPlace(board);
        return { count, minePos };
    }
    function revealAllMinesInPlace(board) {
        for (let r = 0; r < ROWS; r++) {
            for (let c = 0; c < COLS; c++) {
                const cell = board[r][c];
                if (cell.isMine && !cell.isRevealed) {
                    cell.isRevealed = true;
                }
            }
        }
    }
    // Mutates `board` in place: toggles flag at (r, c).
    // Returns true if the flag was toggled, false if no-op (cell already revealed).
    function toggleFlagInPlace(board, r, c) {
        const cell = board[r][c];
        if (cell.isRevealed)
            return false;
        cell.isFlagged = !cell.isFlagged;
        return true;
    }
    return {
        mines: MINES,
        rows: ROWS,
        cols: COLS,
        createEmptyBoard,
        generateBoardInPlace,
        revealCellInPlace,
        countFlaggedAround,
        revealAroundInPlace,
        revealAllMinesInPlace,
        toggleFlagInPlace,
    };
}
export function easyMineSweeper() {
    return mineSweeper(9, 9, 10);
}
export function mediumMineSweeper() {
    return mineSweeper(16, 16, 40);
}
export function hardMineSweeper() {
    return mineSweeper(16, 30, 99);
}
